import React, { useState } from 'react';
import { Form, Badge, Button, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatDistance } from 'date-fns';
import { getDateFnsLocale } from '../shared/utils';
import { useSaving } from '../contexts/SavingContext';

/**
 * PackingItem component for displaying a single packing item
 * 
 * @param {Object} props Component props
 * @param {Object} props.item The packing item object
 * @param {Function} props.onChange Handler for when the item status changes
 * @param {Function} props.onDelete Handler for when the item is deleted
 * @param {Function} props.onEdit Handler for when the item is edited
 */
const PackingItem = ({ item, onChange, onDelete, onEdit, readOnly = false }) => {
  const { t, i18n } = useTranslation();
  const { isSaving } = useSaving();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(item.text);
  const [editCategory, setEditCategory] = useState(item.category || 'Miscellaneous');

  // List of available categories (original English names for internal use)
  const categories = ["Clothing", "Electronics", "Toiletries", "Documents", "Miscellaneous"];
  
  // Get translated categories
  const getTranslatedCategories = () => {
    return categories.map(cat => {
      const categoryKey = cat.toLowerCase();
      return {
        original: cat,
        translated: t(`planDetail.packing.categories.${categoryKey}`, cat)
      };
    });
  };
  
  const translatedCategories = getTranslatedCategories();

  // Format creation date as relative time (e.g., "2 days ago")
  const formattedDate = item.createdAt 
    ? formatDistance(new Date(item.createdAt), new Date(), { 
        addSuffix: true,
        locale: getDateFnsLocale(i18n.language)
      }) 
    : t('planDetail.todo.justNow');
    
  // Handle status change with global saving indicator
  const handleStatusChange = () => {
    if (readOnly || !onChange) return;
    // Call the actual onChange handler
    onChange({
      ...item,
      status: item.status === 'completed' ? 'pending' : 'completed'
    });
  };

  const handleEditClick = () => {
    setEditText(item.text);
    setEditCategory(item.category || 'Miscellaneous');
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (editText.trim()) {
      onEdit({
        ...item,
        text: editText.trim(),
        category: editCategory
      });
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(item.text);
    setEditCategory(item.category || 'Miscellaneous');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Get appropriate badge color based on category
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Clothing':
        return 'info';
      case 'Electronics':
        return 'warning';
      case 'Toiletries':
        return 'success';
      case 'Documents':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="d-flex align-items-start border-bottom py-2">
      <div className="packing-item-checkbox me-2 mt-1">
        <Form.Check 
          type="checkbox"
          checked={item.status === 'completed'}
          onChange={handleStatusChange}
          disabled={isSaving || isEditing}
          aria-label={`Mark "${item.text}" as ${item.status === 'completed' ? 'unpacked' : 'packed'}`}
        />
      </div>
      <div className="flex-grow-1">
        {isEditing ? (
          <>
            <InputGroup className="mb-2">
              <Form.Control
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <Button variant="success" onClick={handleSaveEdit}>
                <i className="bi bi-check"></i>
              </Button>
              <Button variant="secondary" onClick={handleCancelEdit}>
                <i className="bi bi-x"></i>
              </Button>
            </InputGroup>
            <Form.Select 
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              className="mb-2"
              size="sm"
            >
              {translatedCategories.map(({ original, translated }) => (
                <option key={original} value={original}>{translated}</option>
              ))}
            </Form.Select>
          </>
        ) : (
          <>
            <div className={`${item.status === 'completed' ? 'text-decoration-line-through text-muted' : ''}`}>
              {item.text}
            </div>
            <div className="d-flex align-items-center mt-1">
              <small className="text-muted me-2">{t('planDetail.todo.added')} {formattedDate}</small>
              {item.category && (
                <Badge bg={getCategoryColor(item.category)} pill className="me-2" style={{ fontSize: '0.7rem' }}>
                  {translatedCategories.find(({ original }) => original === item.category)?.translated || item.category}
                </Badge>
              )}
            </div>
          </>
        )}
      </div>
      {!readOnly && !isEditing && (
        <div className="packing-item-buttons">
          <Button 
            variant="link" 
            className="text-primary p-0 ms-2" 
            onClick={handleEditClick}
            disabled={isSaving}
            aria-label={`Edit item "${item.text}"`}
          >
            <i className="bi bi-pencil"></i>
          </Button>
          <Button 
            variant="link" 
            className="text-danger p-0 ms-2" 
            onClick={() => onDelete && onDelete(item.id)}
            disabled={isSaving}
            aria-label={`Delete item "${item.text}"`}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      )}
    </div>
  );
};

export default PackingItem; 